import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CALL_INSET, PromptPanel, usePromptCall } from './PromptBadge';
import { reasoningTooltip } from './ReasoningBadge';
import { useAssemblySteps } from './categoryBands';
import {
  COVERS_MODEL, coverFacts, coverPlacementPrompt, NO_COVERS, useCoverPlacements, type CoverFacts,
} from './coverPlacementRun';
import { floorSpots, type FloorSpot } from './covers';
import { holesOfJoint, useJoints, useMeshOf, type Joint } from './holeGroups';
import { usePartsRun } from './partsRun';
import { usePartModelJobs } from './partModels';
import type { HardwareInteraction } from './hardwareInteractionsRun';
import type { StepHole } from './stepHoles';
import { useStepHoles } from './stepHolesRun';
import { useModelSizes } from './partModelSize';

// ONE JOINT'S COVERS AND WHAT THE CALL IS TOLD OF THEM: the holes found in it, and the places its
// parts touch the floor where it lays a cover straight on one (a felt pad). Read by the badge and
// by the joint view alike, from the same data: nothing here needs the scene on screen.
//
// ONE CALL PER COVERS JOINT (see Joint): the buttons over one hardware joint's screws, or the pads
// laid on one frame. `rows` is every row the call places.
export type JointCoverFacts = {
  joint: Joint; rows: HardwareInteraction[]; facts: CoverFacts; holes: StepHole[]; spots: FloorSpot[];
};

// A ROW LAYING ITS COVERS STRAIGHT ON A PART — no hardware under them, so no hole: a felt pad.
export const laysCovers = (r: HardwareInteraction) => r.covers.length > 0 && r.hardware.every((h) => r.covers.includes(h));

export function useJointCoverFacts(docId: string): JointCoverFacts[] {
  const { steps } = useAssemblySteps(docId);
  const { items } = usePartsRun(docId);
  const holes = useStepHoles(docId);
  const models = useModelSizes(docId).byPart;
  const meshOf = useMeshOf(docId);
  const { loaded } = usePartModelJobs();
  const everyJoint = useJoints(docId);
  // EVERY CALL: a covers joint, with its rows.
  const calls = useMemo(() => everyJoint.filter((j) => !j.finds)
    .map((joint) => ({ joint, rows: joint.rows.filter((r) => r.covers.length) })), [everyJoint]);
  // THE FEET, read once off the models of the parts some cover is laid on — every call's, so a
  // spot's id is the same whichever joint asks.
  const laying = useMemo(() => [...new Set(calls.flatMap((c) => c.rows.filter(laysCovers).flatMap((r) => r.components)))]
    .sort((a, b) => Number(a) - Number(b)).join(','), [calls]);
  // NOT READ WHILE IT IS BEING READ AGAIN, and not while the models are: a drilling reads every
  // part's model again (see invalidatePartModels), and meanwhile no part has one — the feet read
  // then were none, kept as the answer while the real ones loaded, and a run placing the pads in
  // that moment wrote "0 feet found, so none were placed" for good.
  const [spots, setSpots] = useState<FloorSpot[] | null>(null);
  useEffect(() => {
    setSpots(null);
    if (!laying || !loaded) return;
    let gone = false;
    void floorSpots(items, meshOf, laying.split(',')).then((got) => { if (!gone) setSpots(got); });
    return () => { gone = true; };
  }, [laying, items, meshOf, loaded]);
  return useMemo(() => calls.flatMap(({ joint, rows }) => {
    const found = holesOfJoint(holes.byStep, steps, joint);
    const laid = rows.filter(laysCovers);
    // Not until the floor is read, where it is asked about: a pad told of no foot has nowhere to go.
    if (laid.length && !spots) return [];
    // UNDER THE PARTS THIS JOINT'S PADS ARE LAID ON, and no other.
    const parts = new Set(laid.flatMap((r) => r.components));
    const under = (spots ?? []).filter((s) => parts.has(s.part));
    return [{
      joint, rows, holes: found, spots: under,
      facts: coverFacts(items, rows, found, under, models),
    }];
  }), [calls, holes.byStep, steps, spots, items, meshOf, models]);
}

// THE FIRST PASS'S ROW: its prompt for one unit's covers, and what it answered.
export function CoverFirstPanel({ docId, unit }: { docId: string; unit: JointCoverFacts }) {
  const { t } = useLanguage();
  const run = useCoverPlacements(docId);
  const mine = run.byJoint[unit.joint.id] ?? NO_COVERS;
  const call = usePromptCall({ load: () => coverPlacementPrompt(unit.facts, COVERS_MODEL), revision: JSON.stringify(unit.facts) });
  useEffect(() => {
    call.open();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <PromptPanel label={t('notice.tip.menuPrompt')} call={call} />
      {mine.passes[0] && run.busy !== unit.joint.id && (
        <div className="mt-6 border-t border-slate-200 pt-4">
          <p className="mb-3 text-center text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t('notice.cat.thinking')}
          </p>
          <div className={CALL_INSET}><PassAnswer pass={mine.passes[0]} /></div>
        </div>
      )}
    </>
  );
}

function PassAnswer({ pass }: { pass: { reasoning: string; raw: string; notes: string } }) {
  const { t } = useLanguage();
  return (
    <>
      {pass.notes && <p className="mb-2 text-center text-sm text-amber-300">{pass.notes}</p>}
      {reasoningTooltip(pass.reasoning, pass.raw, t)}
    </>
  );
}
